/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const FileList = (props)=>{
    const isEditing = useIsEditing();
    const { RenderingIdentifier } = props?.params || {};
    const { headline, description, ctaLink, selectFiles } = props?.fields || {};
    const theme = props?.params?.selectTheme;
    // Helper function to format file size
    const formatFileSize = (size)=>{
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };
    /*
   * Rendering
   */ const { container, list, item, fileIcon, fileInfo, fileSize, headlineText, descriptionText, ctaLinkText, ctaWrapper, rightSection, fileDisplayName, fileTypeIcon, anchor } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/lists/filelist",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        "aria-label": "Downloadable files",
        ...getTestProps(`component-file-list-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("div", {
                className: container(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                editable: true,
                                field: headline,
                                tag: "h2",
                                className: headlineText(),
                                ...getTestProps(`headline`)
                            }),
                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                field: description,
                                className: descriptionText(),
                                ...getTestProps(`description`)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: ctaWrapper(),
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaVariant: "primary",
                                    ctaComponentClass: "component-file-list-button-color-1",
                                    field: ctaLink,
                                    className: ctaLinkText(),
                                    ...getTestProps(`cta`)
                                })
                            })
                        ]
                    }),
                    selectFiles ? /*#__PURE__*/ _jsx("ul", {
                        className: list(),
                        children: selectFiles.map((fl, _index)=>/*#__PURE__*/ _jsx("li", {
                                className: item(),
                                children: /*#__PURE__*/ _jsxs("a", {
                                    href: fl.url,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    className: anchor(),
                                    "aria-label": `Download ${fl.displayName}${fl.fields.Size?.value ? ` (${formatFileSize(Number(fl.fields.Size.value))})` : ''}${fl.fields.Extension?.value ? ` - ${fl.fields.Extension.value.toUpperCase()} file` : ''}`,
                                    ...getTestProps(`file-link-${_index}`),
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: fileInfo(),
                                            children: [
                                                /*#__PURE__*/ _jsx(SvgIcon, {
                                                    icon: "file-download",
                                                    size: "xs",
                                                    className: fileTypeIcon(),
                                                    "aria-hidden": "true"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: fileDisplayName(),
                                                    ...getTestProps(`file-name-${_index}`),
                                                    children: fl.displayName
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: rightSection(),
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: fileSize(),
                                                    ...getTestProps(`file-size-${_index}`),
                                                    children: fl.fields.Size?.value && formatFileSize(Number(fl.fields.Size.value))
                                                }),
                                                fl.fields.Size?.value && fl.fields.Extension?.value && ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: fileIcon(),
                                                    "aria-label": `File type: ${fl.fields.Extension?.value?.toUpperCase()}`,
                                                    ...getTestProps(`file-extension-${_index}`),
                                                    children: fl.fields.Extension?.value && fl.fields.Extension.value.toUpperCase()
                                                })
                                            ]
                                        })
                                    ]
                                })
                            }, fl.id))
                    }) : /*#__PURE__*/ _jsx(_Fragment, {})
                ]
            })
        })
    });
};
export const Default = withStandardComponentWrapper(FileList, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [
            'grid',
            'grid-cols-1',
            'md:grid-cols-2',
            'gap-x-space-between-more',
            'gap-y-4',
            'p-spacing-spacing-32',
            'py-component-section-container-padding-y',
            'px-component-section-container-padding-x',
            'bg-component-file-list-bg'
        ],
        list: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-8',
            'md:gap-spacing-spacing-16'
        ],
        anchor: [
            'flex',
            'items-center',
            'justify-between',
            'w-full',
            'group',
            'p-spacing-spacing-16'
        ],
        item: [
            'bg-component-file-list-card-fill',
            'text-component-file-list-card-text',
            'border-component-file-list-card-border',
            'rounded-component-file-list-card-border-radius',
            'border',
            'transition-colors',
            'duration-200',
            'hover:border-color-text-text-brand',
            'gap-spacing-spacing-16'
        ],
        headlineText: [
            'font-bold',
            'text-component-file-list-title',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge',
            'font-typography-font-family-header',
            'leading-tight',
            'mb-general-spacing-title-margin-bottom',
            'not-italic'
        ],
        descriptionText: [
            'text-component-file-list-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'font-normal',
            'not-italic',
            'leading-typography-line-height-body-large',
            'mb-general-spacing-copy-margin-bottom'
        ],
        ctaWrapper: [
            'flex',
            'justify-start'
        ],
        ctaLinkText: [
            'mt-2'
        ],
        fileInfo: [
            'flex',
            'flex-row',
            'items-center'
        ],
        fileTypeIcon: [
            'flex-shrink-0',
            'mr-2',
            'text-typography-font-size-body-small',
            'text-color-text-text-brand'
        ],
        fileDisplayName: [
            'font-bold',
            'group-hover:text-blue-600',
            'text-typography-font-size-body-small',
            'text-color-text-text-brand',
            'underline'
        ],
        rightSection: [
            'flex',
            'items-center',
            'gap-4',
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary'
        ],
        fileSize: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary'
        ],
        fileIcon: [
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary'
        ]
    }
});
